import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    
    // Initialize EmailJS
    // Replace 'YOUR_PUBLIC_KEY' with your actual EmailJS public key from https://dashboard.emailjs.com/
    emailjs.init('gY0wDAHZ_FqSzXLxV');
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getResponsiveMargin = () => {
    if (screenSize >= 1024) return '100px';
    if (screenSize >= 768) return '48px';
    return '20px';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.subject) errors.subject = 'Please select a subject';
    if (!formData.message.trim()) errors.message = 'Message is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      // Show success message
      setSubmitSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: ''
        });
      }, 3000);
    }
  };

  // Custom icons as React components
  const PhoneIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white-600">
      <path d="M22.0004 16.9201V19.9201C22.0015 20.1986 21.9445 20.4743 21.8329 20.7294C21.7213 20.9846 21.5577 21.2137 21.3525 21.402C21.1472 21.5902 20.905 21.7336 20.6411 21.8228C20.3773 21.912 20.0978 21.9452 19.8204 21.9201C16.7432 21.5857 13.7874 20.5342 11.1904 18.8501C8.77425 17.3148 6.72576 15.2663 5.19042 12.8501C3.5004 10.2413 2.44867 7.27109 2.12042 4.1801C2.09543 3.90357 2.1283 3.62486 2.21692 3.36172C2.30555 3.09859 2.44799 2.85679 2.63519 2.65172C2.82239 2.44665 3.05023 2.28281 3.30422 2.17062C3.5582 2.05843 3.83276 2.00036 4.11042 2.0001H7.11042C7.59574 1.99532 8.06622 2.16718 8.43419 2.48363C8.80216 2.80009 9.0425 3.23955 9.11042 3.7201C9.23705 4.68017 9.47187 5.62283 9.81042 6.5301C9.94497 6.88802 9.97409 7.27701 9.89433 7.65098C9.81458 8.02495 9.62929 8.36821 9.36042 8.6401L8.09042 9.9101C9.51398 12.4136 11.5869 14.4865 14.0904 15.9101L15.3604 14.6401C15.6323 14.3712 15.9755 14.1859 16.3495 14.1062C16.7235 14.0264 17.1125 14.0556 17.4704 14.1901C18.3777 14.5287 19.3203 14.7635 20.2804 14.8901C20.7662 14.9586 21.2098 15.2033 21.5269 15.5776C21.8441 15.9519 21.0126 16.4297 21.1004 16.9201H22.0004Z" stroke="currentColor" strokeWidth="2"></path>
    </svg>
  );

  const EmailIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white-600">
      <g clipPath="url(#clip0_39_210)">
        <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2"></path>
        <path d="M0 0L22 6L12 13L2 6" stroke="currentColor" strokeWidth="2"></path>
      </g>
      <defs>
        <clipPath id="clip0_39_210">
          <rect width="24" height="24" fill="white"></rect>
        </clipPath>
      </defs>
    </svg>
  );

  const ArrowIcon = () => (
    <svg width="10" height="11" viewBox="0 0 10 11" fill="none" className="dropdown-arrow">
      <path d="M9 1H1L5 9L9 1Z" stroke="currentColor"></path>
    </svg>
  );

  return (
    <section style={{ paddingTop: '4rem', paddingBottom: '4rem', paddingLeft: '1rem', paddingRight: '1rem', background: 'linear-gradient(to bottom right, #f8fafc, white)' }} className="md:px-8">
      <div className="mx-auto">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }} className="text-center">
          <h2 style={{ marginBottom: '1rem' }} className="text-4xl md:text-5xl font-bold text-gray-900">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600 mx-auto">
            Have a project in mind? We'd love to hear about it. Send us a message and we'll get back to you within 24 hours.
          </p>
        </div>
        
        <div 
          className="flex flex-col lg:flex-row gap-12 bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100" 
          style={{ margin: getResponsiveMargin() }}
        >
          {/* Contact Info */}
          <div style={{ 
            padding: '2.5rem', 
            background: 'linear-gradient(to bottom right, #182cd4, #1a2eb8)', 
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
            
          }}>
            <div style={{
              display: 'grid',
              gap: '2rem',
              gridTemplateColumns: '1fr',
              marginBottom: '2rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}>
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  transition: 'background-color 0.3s ease',
                  cursor: 'pointer'
                }} 
                onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}>
                  <PhoneIcon />
                </div>
                <div style={{ flex: '1' }}>
                  <h3 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#fff'
                  }}>Phone</h3>
                  <p style={{ 
                    fontSize: '0.95rem', 
                    opacity: '0.9', 
                    marginBottom: '0.25rem',
                    lineHeight: '1.4'
                  }}>+91 6265 865507</p>
                  <p style={{ 
                    fontSize: '0.95rem', 
                    opacity: '0.9', 
                    marginBottom: '0.5rem',
                    lineHeight: '1.4'
                  }}>+91 7725 008610</p>
                  <p style={{ 
                    fontSize: '0.85rem', 
                    opacity: '0.75',
                    fontStyle: 'italic'
                  }}>Mon-Fri 9AM-6PM IST</p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}>
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  transition: 'background-color 0.3s ease',
                  cursor: 'pointer'
                }} 
                onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}>
                  <EmailIcon />
                </div>
                <div style={{ flex: '1' }}>
                  <h3 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#fff'
                  }}>Email</h3>
                  <p style={{ 
                    fontSize: '0.95rem', 
                    opacity: '0.9', 
                    marginBottom: '0.5rem',
                    lineHeight: '1.4'
                  }}>hello@vionexlabs.com</p>
                  <p style={{ 
                    fontSize: '0.85rem', 
                    opacity: '0.75',
                    fontStyle: 'italic'
                  }}>We reply within 24 hours</p>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '4rem' }} className="">
              <div style={{ padding: '1.5rem' }} className="bg-white/10 rounded-xl">
                <h3 style={{ marginBottom: '1rem' }} className="text-xl font-semibold">Our Location</h3>
                <p style={{ marginBottom: '1rem' }} className="text-blue-100">
                  Raipur, Chhattisgarh<br />
                </p>
                <div className="aspect-video rounded-lg flex items-center justify-center" style={{backgroundColor: 'rgba(26, 46, 184, 0.3)'}}>
                  <div className="text-center">
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d118983.72840229067!2d81.61907330000001!3d21.26190555!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a28dda23be28229%3A0x163ee1204ff9e240!2sRaipur%2C%20Chhattisgarh!5e0!3m2!1sen!2sin!4v1748777359314!5m2!1sen!2sin" 
                      width="600" 
                      height="450" 
                      style={{ marginBottom: '0.75rem' }} 
                      className="bg-gray-200 border-2 border-dashed rounded-xl w-100 h-100 mx-auto" 
                      allowFullScreen 
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Raipur Location Map"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Contact Form */}
          <div style={{ padding: '2rem' }} className="lg:w-3/5 md:p-12 flex flex-col justify-center">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label style={{ marginBottom: '0.5rem' }} className="block text-gray-700 font-medium" htmlFor="name">
                    Full Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', color: '#1f2937' }}
                    className={`w-full rounded-xl border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
                    onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#182cd4')}
                    placeholder="John Doe"
                    aria-label="Full Name"
                  />
                  {formErrors.name && <p style={{ marginTop: '0.25rem' }} className="text-red-500 text-sm">{formErrors.name}</p>}
                </div>
                <div>
                  <label style={{ marginBottom: '0.5rem' }} className="block text-gray-700 font-medium" htmlFor="email">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', color: '#1f2937', '--tw-ring-color': '#182cd4' }}
                    className={`w-full rounded-xl border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
                    placeholder="john@company.com"
                    aria-label="Email Address"
                  />
                  {formErrors.email && <p style={{ marginTop: '0.25rem' }} className="text-red-500 text-sm">{formErrors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }} className="block text-gray-700 font-medium" htmlFor="company">
                    Company
                  </label>
                  <input
                    id="company"
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', color: '#1f2937', '--tw-ring-color': '#182cd4' }}
                    className="w-full rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    placeholder="Your Company"
                    aria-label="Company"
                  />
                </div>
                <div>
                  <label style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }} className="block text-gray-700 font-medium" htmlFor="subject">
                    Subject *
                  </label>
                  <div className="relative">
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', color: formData.subject ? '#1f2937' : '#9ca3af', '--tw-ring-color': '#182cd4' }}
                      className={`w-full rounded-xl border ${formErrors.subject ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:border-transparent appearance-none transition-all`}
                      aria-label="Subject"
                    >
                      <option value="">Select a subject</option>
                      <option>General Inquiry</option>
                      <option>Project Request</option>
                      <option>Support</option>
                      <option>Partnership</option>
                    </select>
                    <div style={{ paddingLeft: '0.75rem', paddingRight: '0.75rem' }} className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
                      <ArrowIcon />
                    </div>
                  </div>
                  {formErrors.subject && <p style={{ marginTop: '0.25rem' }} className="text-red-500 text-sm">{formErrors.subject}</p>}
                </div>
              </div>
              <div>
                <label style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }} className="block text-gray-700 font-medium" htmlFor="message">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', color: '#1f2937', '--tw-ring-color': '#182cd4' }}
                  className={`w-full rounded-xl border ${formErrors.message ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
                  placeholder="Tell us about your project..."
                  aria-label="Message"
                ></textarea>
                {formErrors.message && <p style={{ marginTop: '0.25rem' }} className="text-red-500 text-sm">{formErrors.message}</p>}
              </div>
              <div style={{ paddingTop: '1rem' }} className="">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ 
                    paddingLeft: '2rem', 
                    paddingRight: '2rem', 
                    paddingTop: '1rem', 
                    paddingBottom: '1rem',
                    background: submitSuccess ? undefined : 'linear-gradient(to right, #182cd4, #1a2eb8)'
                  }}
                  className={`w-full md:w-full rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center ${
                    submitSuccess 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'shadow-lg hover:shadow-xl'
                  }`}
                  onMouseOver={(e) => !submitSuccess && !isSubmitting && (e.target.style.background = 'linear-gradient(to right, #1a2eb8, #162561)')}
                  onMouseOut={(e) => !submitSuccess && !isSubmitting && (e.target.style.background = 'linear-gradient(to right, #182cd4, #1a2eb8)')}
                  aria-label="Send Message"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : submitSuccess ? (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" style={{marginRight:'10px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" ></path>
                      </svg>
                      Send Message
                    </>
                  )}
                </button>
              </div>
              <div style={{ marginTop: '2.5rem' }} className="flex flex-col items-center">
                <span style={{ marginBottom: '0.5rem' }} className="text-gray-500 text-sm tracking-wide uppercase font-semibold">Connect with us</span>
                <div className="flex justify-center gap-6">
                  <a href="https://www.linkedin.com/company/vionexlabs/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:scale-110 transition-transform">
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="#0A66C2"/><path d="M8.225 17.25V10.5H5.75v6.75h2.475Zm-1.237-7.725c.825 0 1.338-.55 1.338-1.238-.015-.703-.513-1.238-1.323-1.238-.81 0-1.338.535-1.338 1.238 0 .688.513 1.238 1.308 1.238h.015ZM9.75 17.25h2.475v-3.75c0-.2.015-.4.075-.55.163-.4.537-.813 1.163-.813.825 0 1.155.613 1.155 1.513v3.6H17.1v-3.863c0-2.063-1.1-3.025-2.563-3.025-1.188 0-1.713.663-2.013 1.125h.025v-.963H9.75c.025.625 0 6.75 0 6.75Z" fill="#fff"/></svg>
                  </a>
                  <a href="https://instagram.com/vionexlabs" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:scale-110 transition-transform">
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="#E1306C"/><path d="M12 8.4A3.6 3.6 0 1 0 12 15.6 3.6 3.6 0 0 0 12 8.4Zm0 5.9a2.3 2.3 0 1 1 0-4.6 2.3 2.3 0 0 1 0 4.6Zm4.5-6.1a.84.84 0 1 1-1.68 0 .84.84 0 0 1 1.68 0ZM17.25 8.62c-.02-.42-.11-.79-.32-1.13a2.2 2.2 0 0 0-.81-.81c-.34-.21-.71-.3-1.13-.32-.45-.02-.59-.03-1.74-.03s-1.29.01-1.74.03c-.42.02-.79.11-1.13.32-.34.21-.61.48-.81.81-.21.34-.3.71-.32 1.13-.02.45-.03.59-.03 1.74s.01 1.29.03 1.74c.02.42.11.79.32 1.13.21.34.48.61.81.81.34.21.71.3 1.13.32.45.02.59.03 1.74.03s1.29-.01 1.74-.03c.42-.02.79-.11 1.13-.32.34-.21.61-.48.81-.81.21-.34.3-.71.32-1.13.02-.45.03-.59.03-1.74s-.01-1.29-.03-1.74Zm-1.08 4.2a1.4 1.4 0 0 1-.79.79c-.22.09-.45.15-1.36.17-.54.01-.7.02-2.02.02s-1.48-.01-2.02-.02c-.91-.02-1.14-.08-1.36-.17a1.4 1.4 0 0 1-.79-.79c-.09-.22-.15-.45-.17-1.36-.01-.54-.02-.7-.02-2.02s.01-1.48.02-2.02c.02-.91.08-1.14.17-1.36a1.4 1.4 0 0 1 .79-.79c.22-.09.45-.15 1.36-.17.54-.01.7-.02 2.02-.02s1.48.01 2.02.02c.91.02 1.14.08 1.36.17a1.4 1.4 0 0 1 .79.79c.09.22.15.45.17 1.36.01.54.02.7.02 2.02s-.01 1.48-.02 2.02c-.02.91-.08 1.14-.17 1.36Z" fill="#fff"/></svg>
                  </a>
                  <a href="https://facebook.com/vionexlabs" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:scale-110 transition-transform">
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="#1877F3"/><path d="M15.5 8.5h-2V7.5c0-.41.34-.75.75-.75h1.25V5h-2.25A2.25 2.25 0 0 0 11 7.25v1.25H9.5V11H11v6h2.5v-6h1.5l.5-2.5h-2V8.5Z" fill="#fff"/></svg>
                  </a>
                  <a href="https://twitter.com/vionexlabs" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="hover:scale-110 transition-transform">
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="#1DA1F2"/><path d="M19.633 7.997c.013.18.013.36.013.54 0 5.49-4.18 11.82-11.82 11.82-2.35 0-4.54-.69-6.38-1.87.33.04.65.06.99.06 1.95 0 3.75-.66 5.18-1.77-1.82-.03-3.36-1.23-3.89-2.87.25.04.5.07.77.07.37 0 .73-.05 1.07-.14-1.9-.38-3.33-2.06-3.33-4.08v-.05c.56.31 1.2.5 1.88.52a4.13 4.13 0 0 1-1.84-3.44c0-.76.2-1.47.55-2.08a11.7 11.7 0 0 0 8.48 4.3c-.07-.3-.11-.61-.11-.93 0-2.25 1.83-4.08 4.08-4.08 1.18 0 2.25.5 3 1.3a8.1 8.1 0 0 0 2.59-.99c-.09.28-.14.58-.14.89 0 1.01.51 1.9 1.29 2.42a8.2 8.2 0 0 1-2.32.63c.65.51 1.45.81 2.3.81Z" fill="#fff"/></svg>
                  </a>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;